import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const SaudiArabiaPage = () => {
  const saudiData = {
    name: 'Saudi Arabia',
    region: 'GCC',
    color: '#A060B0',
    metaTitle: 'Pharma & Nutraceutical Registration in Saudi Arabia | SFDA | eCTD, Madad',
    metaDescription: 'Learn how to register pharmaceuticals, nutraceuticals, dietary supplements in Saudi Arabia. SFDA requirements, eCTD dossier format, Madad portal steps, timelines, documents.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Saudi Arabia',
      subtitle: 'The GCC largest pharmaceutical market with mature regulatory systems, mandatory eCTD submissions and strict supplement controls - ideal for high-quality exporters with strong dossiers.',
      cta: 'Start Your Saudi Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~36-37 million' },
      { label: 'Income Level', value: 'High-income' },
      { label: 'Healthcare Spend', value: 'Highest in MENA' },
      { label: 'Pharma Market Size', value: 'Largest GCC (USD 10-11B+)' },
      { label: 'Pharma Imports', value: 'High (specialty, biologicals)' },
      { label: 'Supplement Timeline', value: '1-2 months' },
      { label: 'Pharma Timeline', value: 'Several months' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Largest GCC Market + Strong Regulatory Controls',
      stats: [
        'SFDA (Saudi Food & Drug Authority) oversees pharmaceuticals, devices, cosmetics, supplements',
        'eCTD submission mandatory for all pharmaceuticals via Madad portal',
        'Saudi Arabia drives GCC harmonisation',
        'Strong documentation enables leverage across UAE, Qatar, Kuwait, Oman, Bahrain',
        'Imports dominate: chronic therapies, biologics/biosimilars, oncology, high-end generics',
        'Rising nutraceutical demand: urban lifestyle, obesity, diabetes, preventive wellness'
      ],
      opportunities: [
        'High per-capita income and universal healthcare',
        'Large expatriate population',
        'Strong government healthcare infrastructure spending',
        'Continuous demand for chronic and specialty therapies'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: One of the World Highest NCD Rates',
      subtitle: 'Saudi Arabia records very high prevalence of metabolic and lifestyle diseases.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Diabetes: approximately 18-20%',
            'Obesity: about 35-40%',
            'Hypertension: 25-30%',
            'Hyperlipidaemia: widespread',
            'Cardiovascular diseases: major burden',
            'Pharma demand: Antidiabetics (including modern analogues), antihypertensives',
            'Pharma demand: Lipid-lowering agents, anti-obesity/metabolic products, respiratory, allergy'
          ]
        },
        {
          name: 'Wellness & Prevention',
          items: [
            'High obesity and metabolic syndrome burden',
            'Fitness/wellness culture in major cities (Riyadh, Jeddah, Dammam)',
            'Large expatriate workforce',
            'Vitamin D deficiency very common',
            'Nutra opportunities: Metabolic support, joint/bone health',
            'Nutra opportunities: Immunity & vitamins, omega-3 & heart health',
            'Nutra opportunities: Weight-management products'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'SFDA - Saudi Food & Drug Authority',
      description: 'Drug Sector handles Drug Registration (Marketing Authorization). Food Sector handles Food Supplements. Separate directorates for Cosmetics & Herbal products. MADAD (SFDA digital portal) for all drug submissions in eCTD with life-cycle management via sequences. Legal framework: Pharmaceutical Products Law, Herbal Products Regulation, Food Supplement Classification and Standards, GSO Gulf Technical Regulations.',
      pharma: {
        timeline: 'Initial screening, scientific evaluation, committee approval. Typical: several months, longer for biologics/complex. Pricing & reimbursement follow approval. Renewals usually every 5 years. Variations submitted as new eCTD sequences.',
        requirements: [
          'Saudi-licensed agent/Marketing Authorization Holder mandatory',
          'Foreign companies cannot register directly',
          'Full eCTD submissions using CTD Modules 1-5',
          'Module 1: GCC/SFDA regional requirements',
          'Module 2: Overviews & summaries',
          'Module 3: Quality/CMC',
          'Modules 4-5: Non-clinical and clinical data',
          'GMP certificate (SFDA or reference agency approved)',
          'CPP (Certificate of Pharmaceutical Product)',
          'Bioequivalence study (for generics)',
          'Stability data suitable for regional climatic conditions',
          'Label artwork (Arabic + English)',
          'Pricing justification and pharmacoeconomic considerations'
        ],
        insight: 'Saudi Arabia drives GCC regulatory standards. MADAD portal mandatory for all eCTD submissions. Only Saudi-licensed entity can be MAH - no direct foreign registration. Timelines several months with longer periods for biologics. Strong documentation opens access to largest GCC market and facilitates other Gulf approvals.'
      },
      nutra: {
        timeline: 'Supplements: typically 1-2 months. Much faster than pharmaceutical route if correctly classified under SFDA Food Sector.',
        requirements: [
          'Regulated through SFDA Food Sector under GSO standardized supplement regulations',
          'Food Categorization system and Novel ingredient assessment',
          'Strict labelling and claim controls',
          'Food supplement if: vitamins, minerals, amino acids, botanicals within permitted levels',
          'Food-appropriate dosage forms and general wellness/nutritional claims only',
          'Reclassified as drug if: therapeutic/disease claims, high doses exceeding GSO limits',
          'Injectable or pharmaceutical-only forms = drug classification',
          'Local importer/food establishment licence required',
          'Composition details (full formula)',
          'Manufacturer GMP/HACCP/ISO certificates',
          'Label artwork (Arabic + English)',
          'COA (finished product), Stability (when required)',
          'Free Sale Certificate from country of origin',
          'Sports supplements: anti-doping disclaimers required'
        ],
        insight: 'Saudi supplement regulation strict but clear. Classification critical - must stay within GSO permitted levels and avoid therapeutic claims. SFDA Food Sector pathway (1-2 months) much faster than Drug Sector. Sports supplements require specific anti-doping warning. Premium wellness market supports quality products.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Saudi Arabia (Mandatory eCTD)',
      steps: [
        'Local Agent / MAH - Saudi-licensed agent/MAH mandatory. Foreign companies cannot register directly',
        'Dossier Format - eCTD - Full eCTD submissions using CTD Modules 1-5',
        'Module 1: GCC/SFDA regional requirements',
        'Module 2: Overviews & summaries',
        'Module 3: Quality/CMC',
        'Modules 4-5: Non-clinical and clinical data',
        'GMP certificate (SFDA or reference agency approved)',
        'CPP (Certificate of Pharmaceutical Product)',
        'Bioequivalence study (for generics)',
        'Stability data suitable for regional climatic conditions',
        'Label artwork (Arabic + English)',
        'Pricing justification and pharmacoeconomic considerations for selected categories',
        'Assessment: Initial screening, scientific evaluation, committee approval',
        'Typical timeline: several months, longer for biologics/complex',
        'Pricing & reimbursement follow after approval',
        'Renewals: usually every 5 years',
        'Variations: submitted as new eCTD sequences (CBE/major/minor)'
      ]
    },
    
    nutraPathway: {
      title: 'Dietary Supplements & Health Products in Saudi Arabia',
      steps: [
        'Regulated through SFDA Food Sector under GSO standardized supplement regulations',
        'Food Categorization system, Novel ingredient assessment',
        'Strict labelling and claim controls',
        'Food supplement criteria: vitamins, minerals, amino acids, botanicals within permitted levels',
        'Food-appropriate dosage forms required',
        'General wellness/nutritional claims only - no therapeutic claims',
        'Drug classification triggers: therapeutic/disease claims, high doses exceeding GSO limits',
        'Injectable or pharmaceutical-only forms automatically classified as drugs',
        'Local importer/food establishment licence required',
        'Composition details (full formula) mandatory',
        'Manufacturer GMP/HACCP/ISO certificates',
        'Label artwork (Arabic + English) with compliant claims',
        'COA (finished product) required',
        'Stability data (when required)',
        'Free Sale Certificate from country of origin',
        'Sports supplements: anti-doping disclaimers required',
        'Timeline: Supplements typically 1-2 months, Pharma several months'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (SFDA - Drug Sector)': [
        'eCTD sequence 0000 (initial submission)',
        'Administrative docs (MAH, agent, licences)',
        'GMP certificate',
        'CPP / Free Sale Certificate',
        'Quality / CMC details',
        'BE study (for generics)',
        'Non-clinical/clinical summaries',
        'Label & packaging in Arabic/English',
        'Pricing dossier (if required)'
      ],
      'Supplements (SFDA - Food Sector)': [
        'Product classification request (if needed)',
        'Local importer CR & food licence',
        'Formula sheet & COA',
        'Manufacturer GMP/ISO/HACCP',
        'Label files (bilingual)',
        'Safety/claim compliance documentation',
        'Samples for evaluation (when requested)'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Saudi Arabia Belongs in Your GCC Strategy',
      reasons: [
        'Largest pharmaceutical market in the GCC (USD 10-11+ billion)',
        'High import reliance in chronic, specialty & advanced therapies',
        'Dominant NCD burden creates continuous demand',
        'High purchasing power enables premium product acceptance',
        'Strong supplement & wellness culture',
        'Regulatory maturity ensures predictable, structured processes',
        'Success in KSA builds credibility across GCC + MENA'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, statins, cardiology, respiratory, women health, oncology, biosimilars with strong eCTD documentation',
        'Nutra: Metabolic health, omega-3, joint/bone health, vitamins/minerals, antioxidants, weight-management formulas within GSO permitted levels'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Saudi Arabia',
      services: [
        'Portfolio Prioritization for KSA - Identify top SKUs for high-value segments and hospital/retail positioning',
        'Complete eCTD Development - Build/restructure CTD Modules 1-5 specifically for SFDA requirements',
        'Classification Strategy - Ensure supplements stay within permitted GSO limits to avoid drug classification',
        'Label & Claims Localization - Arabic/English alignment, no therapeutic claims for supplements',
        'Local Partner Coordination - Assist in finding right MAH/importer and managing Madad portal submissions',
        'GCC Expansion Plan - Leverage KSA approval to accelerate UAE, Qatar, Kuwait, Oman & Bahrain registrations'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Saudi Arabia?',
        answer: 'SFDA (Saudi Food & Drug Authority) - Drug Sector regulates pharmaceutical products, while Food Sector regulates dietary supplements and food products.'
      },
      {
        question: 'Is eCTD mandatory?',
        answer: 'Yes. All pharmaceutical submissions must be in eCTD format through the Madad portal. This is strictly enforced for all new registrations and variations.'
      },
      {
        question: 'Who can act as MAH?',
        answer: 'Only a Saudi-registered entity can act as Marketing Authorization Holder (MAH). Foreign manufacturers must appoint a local Saudi agent - direct foreign registration is not permitted.'
      },
      {
        question: 'Are supplements regulated separately?',
        answer: 'Yes. Supplements fall under SFDA Food Sector with strict classification, labelling & composition rules. Correct classification is critical to avoid the much longer pharmaceutical Drug Sector pathway.'
      },
      {
        question: 'How long does registration take?',
        answer: 'Supplements: typically 1-2 months via Food Sector. Pharmaceuticals: several months depending on dossier strength and product type (longer for biologics and complex products).'
      }
    ]
  };

  return <CountryTemplate countryData={saudiData} />;
};
